import { COLORS } from '../theme/themeTokens';
import type { V2TransactionType } from '../types/v2';
import { formatTokenAmount } from './amountFormat';

/**
 * History records carry no token precision, so amounts are shown to eight
 * places at most — the server's — through formatTokenAmount: cut, never
 * rounded, two places at least, fewer the larger the amount. `toFixed(2)`
 * showed a 0,0006 BTC withdrawal as "+0,00" and rounded 0,005 BTC up to
 * "0,01".
 */
const HISTORY_AMOUNT_DECIMALS = 8;

export interface SignedAmountStyle {
  /** '+' | '-' | '' — what to prepend to the absolute amount. */
  prefix: string;
  /** Hex color for the amount text. */
  color: string;
}

/**
 * Resolves the visual treatment for a history-row amount strictly from
 * the catalog's `amount_sign` (v1_2_transaction_types). No fallback to
 * the numeric sign of the amount — if the catalog hasn't classified a
 * type as positive or negative ("auto" / null / missing), the row
 * renders plain (no prefix, default text color).
 *
 * Single source of truth for the sign visual across every OneApp
 * history list: BalanceDetailScreen, DepositAccountScreen,
 * TransactionHistoryScreen. CardScreen handles its own (different
 * payment-network amounts) and intentionally does NOT consume this.
 */
export const signedAmountStyle = (
  amountSign: V2TransactionType['amount_sign'] | null | undefined,
): SignedAmountStyle => {
  if (amountSign === 'negative') {
    // `primaryNavy` — same colour the row's type label now uses on
    // the left side (see history screens' txTypeName styles).
    // Negative-ness signalled purely by the leading "-" prefix.
    return { prefix: '-', color: COLORS.primaryNavy };
  }
  if (amountSign === 'positive') {
    return { prefix: '+', color: COLORS.mint };
  }
  return { prefix: '', color: COLORS.textPrimary };
};

/**
 * Render-ready string: "<prefix><absolute amount with spaces>". The
 * stored sign on the amount itself is ignored — we always take the
 * absolute value and prepend the catalog-driven prefix (or no prefix
 * for `auto`/null). NaN amounts pass through unchanged for diagnostic
 * visibility.
 */
export const formatSignedAmount = (
  amount: string,
  amountSign: V2TransactionType['amount_sign'] | null | undefined,
): string => {
  const num = parseFloat(amount);
  if (!Number.isFinite(num)) return amount;
  const { prefix } = signedAmountStyle(amountSign);
  // The backend's own string, sign dropped: a float would add its own tail.
  const magnitude = amount.trim().replace(/^[-+]/, '');
  return `${prefix}${formatTokenAmount(magnitude, HISTORY_AMOUNT_DECIMALS)}`;
};
